import { TripField } from "./TripField";
import { format } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { TripGroup } from "./TripGroup";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import type { ScheduleTripData } from "@/types/scheduletrip";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import { useTdaStore } from "@/stores/tda.store";
import { ReserveTrip } from "@/services/purchase.service";
import { Loader2 } from "lucide-react";

type ModifyCardProps = {
  scheduleTripData: ScheduleTripData | null;
};

const passengerTypes = [
  {
    key: "adultMYS",
    singular: "Adult (Malaysian)",
    plural: "Adults (Malaysian)",
  },
  {
    key: "childMYS",
    singular: "Child (Malaysian)",
    plural: "Children (Malaysian)",
  },
  {
    key: "infantMYS",
    singular: "Infant (Malaysian)",
    plural: "Infants (Malaysian)",
  },
  {
    key: "adultForeign",
    singular: "Adult (Foreign)",
    plural: "Adults (Foreign)",
  },
  {
    key: "childForeign",
    singular: "Child (Foreign)",
    plural: "Children (Foreign)",
  },
  {
    key: "infantForeign",
    singular: "Infant (Foreign)",
    plural: "Infants (Foreign)",
  },
  { key: "local", singular: "Local", plural: "Locals" },
  { key: "foc", singular: "FOC", plural: "FOC" },
  { key: "worker", singular: "Worker", plural: "Workers" },
] as const;

const formatSearchDate = (date?: string) => {
  if (!date) return "-";

  return format(new Date(date.replace(" ", "T")), "EEE, dd MMM yyyy");
};

const formatTime = (time?: string) => {
  if (!time) return "-";

  const [hours, minutes] = time.split(":");

  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export function ModifyCard({ scheduleTripData }: ModifyCardProps) {
  const navigate = useNavigate();
  const verifiedTdaPassengers = useTdaStore(
    (state) => state.verifiedTdaPassengers,
  );

  const [loading] = useState(false);
  const [reserving, setReserving] = useState(false);

  const [searchParams] = useSearchParams();
  const isReturn = searchParams.get("tripType") === "return";

  const searchSummary = scheduleTripData?.searchSummary;

  const passengerSummary =
    passengerTypes
      .map((passengerType) => {
        const quantity = Number(searchParams.get(passengerType.key) ?? 0);

        if (quantity <= 0) return null;

        return `${quantity} ${
          quantity === 1 ? passengerType.singular : passengerType.plural
        }`;
      })
      .filter(Boolean)
      .join(", ") || "Select passengers";

  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();

  const [departureTripId, setDepartureTripId] = useState<string>("");
  const [returnTripId, setReturnTripId] = useState<string>("");

  const departureTrips = scheduleTripData?.departureTripList ?? [];
  const returnTrips = scheduleTripData?.returnTripList ?? [];

  const departureTrip = departureTrips.find(
    (trip) => trip.operatorTripId === departureTripId,
  );
  const returnTrip = returnTrips.find(
    (trip) => trip.operatorTripId === returnTripId,
  );

  const canContinue =
    Boolean(departureTrip) && (!isReturn || Boolean(returnTrip));

  type BuiltTicketItem = {
    operatorTripId: string;
    mdTicketTypeId: string;
    quantity: number;
    price: number;
    isDepart: boolean;
    passengerIdentityNos: string[];
    requiresPassengerIdentity: boolean;
  };

  // const handleNext = () => {
  //   navigate("/purchase-ticket/contact-info");
  // };

  const buildTicketItems = (trip: typeof departureTrip, isDepart: boolean) => {
    if (!trip) return [] as BuiltTicketItem[];

    const grouped = (trip.ticketTypeAvailability ?? [])
      .filter((ticket) => ticket.requestedQuantity > 0)
      .reduce(
        (acc, ticket) => {
          const key = `${ticket.mdTicketTypeId}:${ticket.ticketCategoryCode}`;

          if (!acc[key]) {
            acc[key] = {
              operatorTripId: trip.operatorTripId,
              mdTicketTypeId: ticket.mdTicketTypeId,
              quantity: ticket.requestedQuantity,
              price: ticket.ticketPrice,
              isDepart,
              passengerIdentityNos: verifiedTdaPassengers
                .filter(
                  (passenger) =>
                    passenger.mdTicketTypeId === ticket.mdTicketTypeId,
                )
                .map((passenger) => passenger.passengerIdentityNo),
              requiresPassengerIdentity: ticket.isTDA === true,
            };
          } else {
            // Keep the latest/last price
            acc[key].price = ticket.ticketPrice;
            acc[key].requiresPassengerIdentity =
              acc[key].requiresPassengerIdentity || ticket.isTDA === true;
          }

          return acc;
        },
        {} as Record<
          string,
          {
            operatorTripId: string;
            mdTicketTypeId: string;
            quantity: number;
            price: number;
            isDepart: boolean;
            passengerIdentityNos: string[];
            requiresPassengerIdentity: boolean;
          }
        >,
      );

    return Object.values(grouped);
  };

  const handleNext = async () => {
    if (!canContinue || reserving) return;

    try {
      setReserving(true);

      const agentCompanyId = useAuthStore.getState().user?.agentCompanyId || "";
      const agentCompanySellerId =
        useAuthStore.getState().user?.agentCompanySellerId ?? "";

      if (!agentCompanyId || !agentCompanySellerId) {
        toast.error("Agent information is missing.");
        return;
      }

      const builtTicketItems = [
        ...(departureTrip ? buildTicketItems(departureTrip, true) : []),

        ...(isReturn && returnTrip ? buildTicketItems(returnTrip, false) : []),
      ];

      const hasMissingPassengerIdentity = builtTicketItems.some(
        (item) =>
          item.requiresPassengerIdentity &&
          item.passengerIdentityNos.length < item.quantity,
      );

      if (hasMissingPassengerIdentity) {
        toast.error(
          "Passenger identity is required. Please verify MYKAD/Passport.",
        );
        return;
      }

      const ticketItemList = builtTicketItems.map((item) => ({
        operatorTripId: item.operatorTripId,
        mdTicketTypeId: item.mdTicketTypeId,
        quantity: item.quantity,
        price: item.price,
        isDepart: item.isDepart,
        passengerIdentityNos: item.passengerIdentityNos,
      }));

      const response = await ReserveTrip({
        agentCompanyId,
        agentCompanySellerId,
        mdTerminalIdFrom: searchParams.get("originId") ?? "",
        mdTerminalIdTo: searchParams.get("destinationId") ?? "",
        isReturn,
        ticketItemList,
      });

      if (!response.status) {
        toast.error(response.message ?? "Failed to reserve trip.");
        return;
      }

      navigate("/purchase-ticket/contact-info", {
        state: {
          paymentId: response.data.paymentId,
        },
      });
    } catch (error) {
      console.error("Failed to reserve trip:", error);

      toast.error(
        error instanceof Error ? error.message : "Failed to reserve trip.",
      );
    } finally {
      setReserving(false);
    }
  };

  const summary = isReturn
    ? `${departureTrip ? formatTime(departureTrip.departTime) : "-"} / ${
        returnTrip ? formatTime(returnTrip.departTime) : "-"
      }`
    : departureTrip
      ? formatTime(departureTrip.departTime)
      : "-";

  const displaySelectedTrip = summary || "Select Trip";

  return (
    <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <section className="mt-2 rounded-2xl bg-card px-4 py-6 md:p-6 shadow-xl">
            <h2 className="text-secondary-foreground text-sm font-semibold flex items-center gap-1">
              {searchSummary?.routeName}
            </h2>

            <div className="mt-4 flex items-stretch gap-4">
              {/* TripField components for Departure and Return dates */}
              <TripField
                label="Departure"
                value={formatSearchDate(searchSummary?.departureDate)}
              />

              {isReturn && (
                <>
                  <Separator orientation="vertical" className="h-auto" />
                  <TripField
                    label="Return"
                    value={formatSearchDate(searchSummary?.returnDate)}
                  />
                </>
              )}
            </div>

            <p className="mt-4 text-sm font-medium text-muted">
              {passengerSummary}
            </p>
          </section>

          <TripGroup
            title="Select Departure Trip"
            trips={departureTrips}
            selectedId={departureTripId}
            onSelect={setDepartureTripId}
            loading={loading}
            skeletonCount={loading ? 0 : departureTrips.length}
          />

          {isReturn && (
            <TripGroup
              title="Select Return Trip"
              trips={returnTrips}
              selectedId={returnTripId}
              onSelect={setReturnTripId}
              loading={loading}
              skeletonCount={loading ? 0 : returnTrips.length}
            />
          )}
        </div>
        <div className="hidden md:block">
          <div className="rounded-2xl bg-card px-4 mt-2 py-6 shadow-xl">
            <p className="text-sm mb-3">
              {/* {summary || "Select trips"} */}
              <p className="mb-3 text-sm font-bold text-muted">Select Trip</p>

              <p className="mb-3 text-sm font-medium text-secondary-foreground">
                {summary}
              </p>
            </p>
            <Button
              type="button"
              variant="secondary"
              className="w-full cursor-pointer"
              disabled={!canContinue || reserving}
              onClick={handleNext}
            >
              {reserving ? (
                <>
                  <Loader2 className="animate-spin" />
                  Next...
                </>
              ) : (
                "Next"
              )}
            </Button>
          </div>
        </div>
      </div>

      <footer
        ref={footerRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
          <p className="text-sm font-bold text-muted-foreground">
            {displaySelectedTrip}
          </p>
          <Button
            type="button"
            variant="secondary"
            className="min-w-40 cursor-pointer"
            disabled={!canContinue || reserving}
            onClick={handleNext}
          >
            {reserving ? (
              <>
                <Loader2 className="animate-spin" />
                Next...
              </>
            ) : (
              "Next"
            )}
          </Button>
        </div>
      </footer>
    </>
  );
}
